import React, { useState, useMemo, useEffect } from "react";
import type { ReactNode } from "react";
import {
  Table as MuiTable,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableContainer,
  Paper,
  CircularProgress,
  Typography,
  Box,
  IconButton,
  Checkbox,
  Button,
  Chip,
} from "@mui/material";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import UnfoldMoreIcon from "@mui/icons-material/UnfoldMore";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import FileCopyIcon from "@mui/icons-material/FileCopy";
import VisibilityIcon from "@mui/icons-material/Visibility";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import { useAppDispatch } from "../../../Redux/Store/Hooks";
import { StudyMaterialPreview } from "../../../Redux/Slices/Booklet/Tutor/StudyMaterial/StudyMaterialPreview";
import StudyMaterialPreviewDialog from "../PreviewDialog/PreviewDialod";
import EmptyCart from "../../assets/images/Empty_Cart.svg";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import ArrowBackIosIcon from "@mui/icons-material/ArrowBackIos";

export interface CartTableColumn<T> {
  key: keyof T;
  title: string;
  sortable?: boolean;
  width?: string | number;
  align?: "left" | "center" | "right";
  render?: (value: T[keyof T], record: T, index: number) => ReactNode;
}

export interface TableProps<T> {
  columns: CartTableColumn<T>[];
  data: T[];
  loading?: boolean;
  emptyMessage?: string;
  onRowClick?: (record: T, index: number) => void;
  rowClassName?: (record: T, index: number) => string;
  onQuantityChange?: (record: T, index: number, newQuantity: number) => void;
  pageType?: "cart" | "details" | string;
  getPreviewId?: (record: T) => string | number | null;
}

type SortOrder = "asc" | "desc" | null;

// ✅ Chip Styles for Dates
const getDateChipProps = (value: string) => {
  const lowerValue = value.toLowerCase();

  if (lowerValue.includes("today")) {
    return {
      label: value,
      sx: {
        backgroundColor: "#E8F5E8",
        color: "#2E7D32",
        border: "1px solid #A5D6A7",
        fontWeight: 500,
      },
    };
  } else if (lowerValue.includes("yesterday")) {
    return {
      label: value,
      sx: {
        backgroundColor: "#FFF3E0",
        color: "#E65100",
        border: "1px solid #FFCC02",
        fontWeight: 500,
      },
    };
  } else {
    return {
      label: value,
      sx: {
        backgroundColor: "#F5F5F5",
        color: "#424242",
        border: "1px solid #BDBDBD",
        fontWeight: 500,
      },
    };
  }
};

const CartCommanTable = <T extends Record<string, unknown>>({
  columns,
  data,
  loading = false,
  emptyMessage = "No data available",
  onRowClick,
  rowClassName,
  onQuantityChange,
  getPreviewId,
  pageType,
}: TableProps<T>) => {
  const [sortConfig, setSortConfig] = useState<{
    key: keyof T | null;
    direction: SortOrder;
  }>({ key: null, direction: null });

  const [page, setPage] = useState(0);
  const rowsPerPage = 10;
  const [previewDialogOpen, setPreviewDialogOpen] = useState(false);
  const [selectedPreviewData, setSelectedPreviewData] = useState<T | null>(
    null
  );

  const dispatch = useAppDispatch();

  const handleSort = (key: keyof T) => {
    let direction: SortOrder = "asc";
    if (sortConfig.key === key && sortConfig.direction === "asc")
      direction = "desc";
    else if (sortConfig.key === key && sortConfig.direction === "desc")
      direction = null;
    setSortConfig({ key: direction ? key : null, direction });
  };

  const handlePreviewClick = (row: T) => {
    const previewId: string | number | null =
      (getPreviewId ? getPreviewId(row) : undefined) ??
      (row as any).id ??
      (row as any).cart_id ??
      null;

    if (!previewId) {
      console.warn("No preview ID found for row:", row);
      return;
    }

    dispatch(StudyMaterialPreview({ id: previewId }));
    setSelectedPreviewData(row);
    setPreviewDialogOpen(true);
  };

  const handleClosePreviewDialog = () => {
    setPreviewDialogOpen(false);
    setSelectedPreviewData(null);
  };

  const sortedData = useMemo(() => {
    if (!sortConfig.key || !sortConfig.direction) return data;
    return [...data].sort((a, b) => {
      const aVal = a[sortConfig.key!];
      const bVal = b[sortConfig.key!];
      if (typeof aVal === "number" && typeof bVal === "number")
        return sortConfig.direction === "asc" ? aVal - bVal : bVal - aVal;
      if (typeof aVal === "string" && typeof bVal === "string")
        return sortConfig.direction === "asc"
          ? aVal.localeCompare(bVal)
          : bVal.localeCompare(aVal);
      return 0;
    });
  }, [data, sortConfig]);

  const paginatedData = useMemo(() => {
    const totalPages = Math.ceil(sortedData.length / rowsPerPage);

    // Determine the correct page
    let currentPage = page;
    if (sortedData.length === 0) {
      currentPage = 0;
    } else if (page >= totalPages) {
      currentPage = totalPages - 1;
    }

    // If page needs correction, schedule update
    if (currentPage !== page) {
      queueMicrotask(() => setPage(currentPage));
    }

    const start = currentPage * rowsPerPage;
    return sortedData.slice(start, start + rowsPerPage);
  }, [sortedData, page, rowsPerPage]);

  const totalPages = useMemo(
    () => Math.ceil(sortedData.length / rowsPerPage),
    [sortedData.length, rowsPerPage]
  );

  // Compute a "safe" page number before slicing
  const safePage = useMemo(() => {
    if (totalPages === 0) return 0; // no data at all
    if (page >= totalPages) return totalPages - 1; // page too high, go to last valid page
    return page; // valid page
  }, [page, totalPages]);

  // Sync the actual page state when the safePage differs
  useEffect(() => {
    if (page !== safePage) {
      setPage(safePage);
    }
  }, [safePage, page]);

  useEffect(() => {
    if (loading) {
      setPage(0);
    }
  }, [loading]);

  const getSortIcon = (columnKey: keyof T) => {
    if (sortConfig.key !== columnKey || !sortConfig.direction)
      return <UnfoldMoreIcon fontSize="small" sx={{ color: "#6B7280" }} />;
    if (sortConfig.direction === "asc")
      return <ArrowUpwardIcon fontSize="small" sx={{ color: "#111827" }} />;
    return <ArrowDownwardIcon fontSize="small" sx={{ color: "#111827" }} />;
  };

  if (loading) {
    return (
      <Box
        display="flex"
        alignItems="center"
        justifyContent="center"
        p={4}
        component={Paper}
        elevation={0}
        sx={{ border: "1px solid #E5E7EB", borderRadius: "8px" }}
      >
        <CircularProgress size={28} />
      </Box>
    );
  }

  return (
    <>
      <Paper
        elevation={0}
        sx={{
          border: "1px solid #E5E7EB",
          borderRadius: "8px",
          boxShadow: "0px 1px 2px rgba(0,0,0,0.05)",
          backgroundColor: "#fff",
        }}
      >
        <TableContainer
          sx={{ maxHeight: 570, width: "100%", overflowX: "auto" }}
        >
          <MuiTable
            stickyHeader
            sx={{
              borderCollapse: "collapse",
              "& th, & td": {
                border: "none",
              },
              "& th": {
                backgroundColor: "#F9FAFB",
                color: "#111827",
                fontSize: "14px",
                fontWeight: 600,
                textAlign: "center",
              },
              "& td": {
                fontSize: "14px",
                fontWeight: 500,
                color: "#374151",
                textAlign: "center",
              },
              "& tr:not(:last-of-type) td": {
                borderBottom: "none",
              },
            }}
          >
            <TableHead>
              <TableRow>
                {/* <TableCell sx={{ width: "60px" }}>S.No</TableCell> */}
                {columns.map((column) => (
                  <TableCell
                    key={String(column.key)}
                    align={column.align ?? "center"}
                    style={{ width: column.width }}
                  >
                    <Box
                      display="flex"
                      alignItems="center"
                      justifyContent="center"
                    >
                      {column.title}
                      {column.sortable && (
                        <IconButton
                          size="small"
                          onClick={() => handleSort(column.key)}
                          sx={{ ml: 0.5 }}
                        >
                          {getSortIcon(column.key)}
                        </IconButton>
                      )}
                    </Box>
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>

            <TableBody>
              {!loading && paginatedData.length > 0 ? (
                paginatedData.map((row, index) => (
                  <TableRow
                    key={index}
                    hover
                    onClick={() => onRowClick?.(row, index)}
                    sx={{
                      cursor: onRowClick ? "pointer" : "default",
                    }}
                    className={rowClassName ? rowClassName(row, index) : ""}
                  >
                    {/* <TableCell>{page * rowsPerPage + index + 1}</TableCell> */}

                    {columns.map((column) => {
                      let content: ReactNode = null;
                      const value = row[column.key];

                      if (
                        column.key === "fileType" ||
                        column.key === "file_name"||
                        column.key === "File"
                      ) {
                        const fileName = String(value ?? "");
                        const isPdf = fileName.toLowerCase().endsWith(".pdf");

                        content = (
                          <Box
                            display="flex"
                            alignItems="center"
                            justifyContent="center"
                            gap={1}
                          >
                            <Typography
                              sx={{ fontSize: "14px", fontWeight: 500 }}
                            >
                              {fileName}
                            </Typography>
                            {isPdf ? (
                              <PictureAsPdfIcon
                                color="error"
                                fontSize="small"
                              />
                            ) : (
                              <FileCopyIcon
                                sx={{ color: "#009DFF" }}
                                fontSize="small"
                              />
                            )}
                          </Box>
                        );
                      } else if (column.key === "addCart") {
                        content = (
                          <Checkbox
                            checked={Boolean(value)}
                            onChange={() => console.log("Cart toggled", row)}
                          />
                        );
                      } else if (
                        column.key === "lastUpdate" ||
                        column.key === "modifiedTime" ||
                        column.key === "date" ||
                        column.key === "updated_on"
                      ) {
                        if (value && typeof value === "string") {
                          const chipProps = getDateChipProps(value);
                          content = <Chip {...chipProps} size="small" />;
                        } else {
                          content = value as ReactNode;
                        }
                      } else if (column.key === "preview") {
                        content = (
                          <IconButton
                            size="small"
                            sx={{ color: "#009DFF" }}
                            onClick={(e) => {
                              e.stopPropagation();
                              handlePreviewClick(row);
                            }}
                          >
                            <VisibilityIcon fontSize="small" />
                          </IconButton>
                        );
                      } else if (column.render) {
                        content = column.render(value, row, index);
                      } else {
                        content = value as ReactNode;
                      }

                      return (
                        <TableCell
                          key={String(column.key)}
                          align={column.align ?? "center"}
                        >
                          {content}
                        </TableCell>
                      );
                    })}
                  </TableRow>
                ))
              ) : !loading && data.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={columns.length + 1} align="center">
                    <Box
                      display="flex"
                      flexDirection="column"
                      alignItems="center"
                      justifyContent="center"
                      py={2}
                    >
                      <img
                        src={EmptyCart}
                        alt="Empty Cart"
                        style={{ width: "150px", marginBottom: "16px" }}
                      />
                    </Box>
                  </TableCell>
                </TableRow>
              ) : null}
            </TableBody>
          </MuiTable>
        </TableContainer>
        {totalPages > 1 && (
          <Box display="flex" alignItems="center" p={2} gap={"8px"}>
            <Box>
              <IconButton
                onClick={() => setPage((prev) => Math.max(prev - 1, 0))}
                disabled={page === 0}
                size="small"
              >
                <ArrowBackIosIcon fontSize="small" />
              </IconButton>

              <IconButton
                onClick={() =>
                  setPage((prev) => Math.min(prev + 1, totalPages - 1))
                }
                disabled={page >= totalPages - 1}
                size="small"
              >
                <ArrowForwardIosIcon fontSize="small" />
              </IconButton>
            </Box>

            <Typography sx={{ fontSize: "12px", fontWeight: "600" }}>
              Page {page + 1} of {totalPages || 1}
            </Typography>
          </Box>
        )}
      </Paper>

      <StudyMaterialPreviewDialog
        open={previewDialogOpen}
        onClose={handleClosePreviewDialog}
      />
    </>
  );
};

export default CartCommanTable;
